import { storeToRefs } from 'pinia'
import { useUserStore } from '~/store/user'
import { buildLogoutUrl, getHosts } from '~/shared/utils/env'
import { useUserToken } from '~/composables/use-user-token'

export const useAuth = () => {
  const { $services, $axios } = useNuxtApp()
  const userStore = useUserStore()
  const config = useRuntimeConfig()
  const { removeAllTokens } = useUserToken()
  const { authorizedUser } = storeToRefs(userStore)
  const { setAuthorizedUser, clearUser } = userStore

  const loading = ref(false)
  const error = ref<Error | null>(null)

  const isAuth = computed(() => !!authorizedUser.value)

  const getAuthUser = async (): Promise<boolean> => {
    loading.value = true
    error.value = null

    try {
      const userData = await $services.userService.getAuthorizedUser()
      if (userData) {
        setAuthorizedUser(userData)
        return true
      } else {
        clearUser()
        return false
      }
    } catch (err) {
      error.value = err as Error
      clearUser()
      return false
    } finally {
      loading.value = false
    }
  }

  const logout = () => {
    clearUser()
    removeAllTokens()

    if ($axios) {
      delete $axios.defaults.headers.common['Authorization-SF-Token']
    }

    if (import.meta.client) {
      const hosts = getHosts({
        NUXT_PUBLIC_ENVIRONMENT_STAND: config.public.environmentStand,
      })

      // без выхода из SSO сессия восстановится при следующем заходе
      window.location.href = buildLogoutUrl(hosts, `${window.location.origin}/`)
    }
  }

  const initAuth = async (): Promise<void> => {
    await getAuthUser()
  }

  return {
    user: readonly(authorizedUser),
    loading: readonly(loading),
    error: readonly(error),
    isAuth,
    getAuthUser,
    logout,
    initAuth,
  }
}
